import type * as React from "react";
import {useMemo, useState} from "react";

import {getPromptVariable} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {useUserAttributes} from "@khan/shared-core";
import type {ReferenceQuery, ReferencesContext} from "@khan/slate-contexts";

import {useCoacheeNamesWithClassroomFilter} from "../queries/use-coachee-names-with-classroom-filter-queries.ts";
import {filterIncludes} from "../utils/filter-references.ts";

/**
 * This hook is used to initialize the references context.
 * It should only be used once to pass the result to the provider.
 */
export const useReferencesInitializer = (): React.ContextType<
    typeof ReferencesContext
> => {
    const [disabled, setDisabled] = useState(true);
    const [query, setQuery] = useState<ReferenceQuery>({
        search: "",
        range: null,
    });
    const [index, setIndex] = useState(0);

    // query everything we need (that's how react works) and check below
    //   what references should be shown
    const userAttributes = useUserAttributes();
    const userAsStudent = useGuideState("userAsStudent");
    const coacheeNames = useCoacheeNamesWithClassroomFilter(
        getPromptVariable("classroomDescriptor"),
    );

    // memoizing things that will not need to be recomputed
    const {contexts, refList, enabledConfigs} = useMemo(() => {
        const hasClasses = userAttributes?.hasClasses;
        const hasStudents = userAttributes?.hasCoachees;
        const teacherCanUseRef = hasClasses && hasStudents && !userAsStudent;
        const hasChildStudents = userAttributes?.hasChildren;
        const isParent = userAttributes?.hasAccessToAIGuideParent;
        const parentCanUseRef = isParent && hasChildStudents;

        // as I understand, references will be used in different contexts,
        //   so exposing why the reference button should be shown
        //   will enable us to build a reference list that includes
        //   all appropriate sources for all valid contexts
        const contexts: Array<string> = [];
        if (teacherCanUseRef) {
            contexts.push("classroom");
        }
        if (parentCanUseRef) {
            contexts.push("parent");
        }

        // build refs list and filter it
        const refList: Array<string> = [];

        if (contexts.includes("classroom") || contexts.includes("parent")) {
            refList.push(...coacheeNames);
        }

        const enabledConfigs = new Set<string>();

        if (contexts.includes("classroom")) {
            enabledConfigs.add("activity-class-snapshot");
            enabledConfigs.add("activity-assignment-recommendations");
            enabledConfigs.add("activity-get-chat-summary-for-teacher");
        }
        if (contexts.includes("parent")) {
            enabledConfigs.add("activity-get-chat-summary-for-parent");
        }

        return {contexts, refList, enabledConfigs};
    }, [userAttributes, coacheeNames, userAsStudent]);

    const filteredReferenceList = filterIncludes(query.search, refList, 5);

    return {
        disabled,
        setDisabled,
        query,
        setQuery,
        index,
        setIndex,
        filteredReferenceList,
        showButton: contexts.length > 0,
        enabledConfigs,
        contexts,
    };
};
